import apiClient from './index';
import {
  IntegratorApiTokenCreate,
  IntegratorApiTokenCreated,
  IntegratorApiTokenRead,
  LoRaWanConnectionTestResult,
  LoRaWanDiagnosticsCleanupRequest,
  LoRaWanDiagnosticsCleanupResponse,
  LoRaWanDiagnosticsSettingsRead,
  LoRaWanDiagnosticsSettingsUpdate,
  LoRaWanGatewayEventBufferRead,
  LoRaWanProfileMappingPayload,
  LoRaWanProfileMappingRead,
  LoRaWanProviderSettingsRead,
  LoRaWanProviderSettingsUpdate,
  LoRaWanRemoteProfileRead,
  TenantLoRaWanApplicationPayload,
  TenantLoRaWanApplicationRead,
  TenantLoRaWanBindingRead,
  TenantLoRaWanBindingUpdate,
} from './types';

export const integrationsApi = {
  async listTokens() {
    const res = await apiClient.get<IntegratorApiTokenRead[]>('/admin/integrations/tokens');
    return res.data;
  },

  async createToken(payload: IntegratorApiTokenCreate) {
    const res = await apiClient.post<IntegratorApiTokenCreated>('/admin/integrations/tokens', payload);
    return res.data;
  },

  async revokeToken(tokenId: string) {
    await apiClient.delete(`/admin/integrations/tokens/${tokenId}`);
  },

  async getLoRaWanSettings() {
    const res = await apiClient.get<LoRaWanProviderSettingsRead | null>('/admin/lorawan/settings');
    return res.data;
  },

  async updateLoRaWanSettings(payload: LoRaWanProviderSettingsUpdate) {
    const res = await apiClient.put<LoRaWanProviderSettingsRead>('/admin/lorawan/settings', payload);
    return res.data;
  },

  async testLoRaWanSettings() {
    const res = await apiClient.post<LoRaWanConnectionTestResult>('/admin/lorawan/settings/test');
    return res.data;
  },

  async getLoRaWanDiagnosticsSettings() {
    const res = await apiClient.get<LoRaWanDiagnosticsSettingsRead>('/admin/lorawan/diagnostics/settings');
    return res.data;
  },

  async updateLoRaWanDiagnosticsSettings(payload: LoRaWanDiagnosticsSettingsUpdate) {
    const res = await apiClient.put<LoRaWanDiagnosticsSettingsRead>('/admin/lorawan/diagnostics/settings', payload);
    return res.data;
  },

  async listLoRaWanTenantBindings() {
    const res = await apiClient.get<TenantLoRaWanBindingRead[]>('/admin/lorawan/tenants');
    return res.data;
  },

  async updateLoRaWanTenantBinding(tenantId: string, payload: TenantLoRaWanBindingUpdate) {
    const res = await apiClient.put<TenantLoRaWanBindingRead>(
      `/admin/lorawan/tenants/${tenantId}/binding`,
      payload
    );
    return res.data;
  },

  async listLoRaWanTenantApplications(tenantId: string) {
    const res = await apiClient.get<TenantLoRaWanApplicationRead[]>(
      `/admin/lorawan/tenants/${tenantId}/applications`
    );
    return res.data;
  },

  async createLoRaWanTenantApplication(tenantId: string, payload: TenantLoRaWanApplicationPayload) {
    const res = await apiClient.post<TenantLoRaWanApplicationRead>(
      `/admin/lorawan/tenants/${tenantId}/applications`,
      payload
    );
    return res.data;
  },

  async updateLoRaWanTenantApplication(applicationId: string, payload: TenantLoRaWanApplicationPayload) {
    const res = await apiClient.put<TenantLoRaWanApplicationRead>(
      `/admin/lorawan/tenant-applications/${applicationId}`,
      payload
    );
    return res.data;
  },

  async deleteLoRaWanTenantApplication(applicationId: string) {
    await apiClient.delete(`/admin/lorawan/tenant-applications/${applicationId}`);
  },

  async listLoRaWanRemoteProfiles(externalTenantId?: string | null) {
    const res = await apiClient.get<LoRaWanRemoteProfileRead[]>('/admin/lorawan/remote-profiles', {
      params: {
        external_tenant_id: externalTenantId || undefined,
      },
    });
    return res.data;
  },

  async listLoRaWanGatewayBuffer(params?: { gateway_id?: string; event_type?: string; limit?: number }) {
    const res = await apiClient.get<LoRaWanGatewayEventBufferRead[]>('/admin/lorawan/diagnostics/buffer', {
      params,
    });
    return res.data;
  },

  async cleanupLoRaWanDiagnostics(payload: LoRaWanDiagnosticsCleanupRequest) {
    const res = await apiClient.post<LoRaWanDiagnosticsCleanupResponse>(
      '/admin/lorawan/diagnostics/cleanup',
      payload
    );
    return res.data;
  },

  async listLoRaWanProfileMappings() {
    const res = await apiClient.get<LoRaWanProfileMappingRead[]>('/admin/lorawan/profile-mappings');
    return res.data;
  },

  async createLoRaWanProfileMapping(payload: LoRaWanProfileMappingPayload) {
    const res = await apiClient.post<LoRaWanProfileMappingRead>('/admin/lorawan/profile-mappings', payload);
    return res.data;
  },

  async updateLoRaWanProfileMapping(mappingId: string, payload: LoRaWanProfileMappingPayload) {
    const res = await apiClient.put<LoRaWanProfileMappingRead>(
      `/admin/lorawan/profile-mappings/${mappingId}`,
      payload
    );
    return res.data;
  },

  async deleteLoRaWanProfileMapping(mappingId: string) {
    await apiClient.delete(`/admin/lorawan/profile-mappings/${mappingId}`);
  },
};
